import { Button, FormField, Input, ModalDialog, Numeric, Select } from "@rothoblaas/design-system";
import { FC, useContext, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useI18n from "../../../../hooks/useTranslations";
import type { CurvePoint } from "../../../../http/envelopeDesigner/types/curvePoint";
import type { Material, ProvenanceField, ThicknessMode } from "../../../../http/envelopeDesigner/types/material";
import { MaterialBody, useAdminEnvelopeDesignerApi } from "../../../../http/envelopeDesigner/useAdminEnvelopeDesigner";
import styles from "./AdminForm.module.css";

type Option<T> = { value: T; label: string };

const emptyBody: MaterialBody = {
  name: "",
  nameIt: null,
  nameDe: null,
  nameFr: null,
  nameEs: null,
  manufacturer: null,
  category: "other",
  subcategory: null,
  density: 0,
  lambdaDry: 0,
  cp: 0,
  mu: null,
  wCap: null,
  emissivity: 0.9,
  hatch: null,
  thicknessMode: "variable",
  knownThicknessesMm: null,
  defaultThicknessMm: 50,
  minThicknessMm: 1,
  maxThicknessMm: 500,
  sdVariableMin: null,
  sdVariableMax: null,
  dryCupPerm: null,
  hygricThetaPor: null,
  hygricThetaEff: null,
  hygricThetaCap: null,
  hygricTheta80: null,
  hygricAw: null,
  moistureCurve: [],
  lambdaMoistureCurve: [],
  muMoistureCurve: [],
  sdHumidityCurve: [],
  suctionCurve: [],
  liquidConductivityCurve: [],
  datasheetUrl: null,
  productPageUrl: null,
  datasheetChecked: null,
  sourceDatabase: null,
  sourceLaboratory: null,
  sourceDate: null,
  sourceFile: null,
  assumedFields: [],
  derivedFields: [],
  dataNote: null,
  dataNoteLambda: null,
  formerlyNamed: null,
  condCategory: "none",
};

/** The translated names (field suffix) and their language; null = the English name. */
const TRANSLATIONS = [
  ["It", "IT"],
  ["De", "DE"],
  ["Fr", "FR"],
  ["Es", "ES"],
] as const;
const PROVENANCE_FIELDS: ProvenanceField[] = ["lambdaDry", "density", "cp", "mu", "emissivity", "wCap"];
const DATA_NOTES = ["", "lambdaBelowAir", "densityDerived"] as const;

/** The curves as the admin types them: a JSON list of points, x and y under the keys of the curve. */
type CurveField =
  | "moistureCurve"
  | "lambdaMoistureCurve"
  | "muMoistureCurve"
  | "sdHumidityCurve"
  | "suctionCurve"
  | "liquidConductivityCurve";
const CURVES: { field: CurveField; x: string; y: string }[] = [
  { field: "moistureCurve", x: "rh", y: "w" },
  { field: "lambdaMoistureCurve", x: "w", y: "value" },
  { field: "muMoistureCurve", x: "w", y: "value" },
  { field: "sdHumidityCurve", x: "rh", y: "sd" },
  { field: "suctionCurve", x: "log10pc", y: "w" },
  { field: "liquidConductivityCurve", x: "w", y: "log10kl" },
];

const toBody = ({ id: _id, code: _code, visibility: _vis, order: _o, ...body }: Material): MaterialBody => body;

const curveText = (curve: CurvePoint[], x: string, y: string) =>
  curve.length ? JSON.stringify(curve.map((p) => ({ [x]: p.x, [y]: p.y }))) : "";
/** A curve typed as a JSON list of {x, y} points under its keys; null: not such a list. */
function parseCurve(text: string, x: string, y: string): CurvePoint[] | null {
  if (!text.trim()) return [];
  try {
    const points: unknown = JSON.parse(text);
    if (!Array.isArray(points)) return null;
    const curve = points.map((p) => ({ x: p?.[x], y: p?.[y] }));
    return curve.every((p) => typeof p.x === "number" && typeof p.y === "number") ? curve : null;
  } catch {
    return null;
  }
}

// Edit or create a catalog material. The server validates the same rules as for custom materials
// (calculation_service.go materialPropsProblem) and answers 400/409 with a message.
const MaterialFormDialog: FC<{ material: Material | null; onClose: () => void }> = ({ material, onClose }) => {
  const i18n = useI18n();
  const t = i18n.translation.envelopeDesignerModule.admin;
  const m = i18n.translation.envelopeDesignerModule.materialModal;
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminEnvelopeDesignerApi();
  const [body, setBody] = useState<MaterialBody>(() => (material ? toBody(material) : emptyBody));
  const [knownText, setKnownText] = useState((material?.knownThicknessesMm ?? []).join(", "));
  const [curveTexts, setCurveTexts] = useState<Record<CurveField, string>>(
    () =>
      Object.fromEntries(CURVES.map((c) => [c.field, curveText(material?.[c.field] ?? [], c.x, c.y)])) as Record<
        CurveField,
        string
      >,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const subcategories = adminApi.useAdminMaterialSubcategories().data?.items ?? [];
  const categories = adminApi.useAdminMaterialCategories().data?.items ?? [];
  const condCategories = adminApi.useAdminCondCategories().data?.items ?? [];
  const update = (patch: Partial<MaterialBody>) => setBody((current) => ({ ...current, ...patch }));

  const save = async () => {
    const curves: Partial<Record<CurveField, CurvePoint[]>> = {};
    for (const c of CURVES) {
      const curve = parseCurve(curveTexts[c.field], c.x, c.y);
      if (!curve) {
        dispatch(showErrorNotification(i18n, new Error(t.invalidCurve(t[c.field]))));
        return;
      }
      curves[c.field] = curve;
    }
    const knownThicknessesMm = knownText
      .split(",")
      .map((value) => Number(value.trim()))
      .filter((value) => value > 0);
    setIsSubmitting(true);
    try {
      await adminApi.saveMaterial(material?.id ?? null, {
        ...body,
        knownThicknessesMm: knownThicknessesMm.length ? knownThicknessesMm : null,
        ...curves,
      });
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const number = (label: string, field: keyof MaterialBody, decimalScale: number, optional = false) => (
    <FormField label={label} isOptional={optional}>
      {({ labelId }) => (
        <Numeric
          id={labelId}
          value={(body[field] as number | null) ?? null}
          decimalScale={decimalScale}
          allowNegative={false}
          onValueChange={(value) => update({ [field]: optional ? value : (value ?? 0) })}
        />
      )}
    </FormField>
  );

  type TextField =
    | "nameIt"
    | "nameDe"
    | "nameFr"
    | "nameEs"
    | "datasheetUrl"
    | "productPageUrl"
    | "sourceDatabase"
    | "sourceLaboratory"
    | "sourceDate"
    | "sourceFile"
    | "formerlyNamed";
  const text = (label: string, field: TextField) => (
    <FormField key={field} label={label} isOptional>
      {({ labelId }) => (
        <Input
          id={labelId}
          value={body[field] ?? ""}
          onChange={(event) => update({ [field]: event.target.value || null })}
        />
      )}
    </FormField>
  );

  const fieldChecks = (label: string, field: "assumedFields" | "derivedFields") => (
    <div className={`${styles.wide} ${styles.checks}`}>
      {label}:
      {PROVENANCE_FIELDS.map((f) => (
        <label key={f}>
          <input
            type="checkbox"
            checked={body[field].includes(f)}
            onChange={(event) =>
              update({
                [field]: event.target.checked ? [...body[field], f] : body[field].filter((other) => other !== f),
              })
            }
          />{" "}
          {f}
        </label>
      ))}
    </div>
  );

  // labelOf: the option's text (default: the value itself, "—" for none)
  const select = <T extends string>(
    label: string,
    value: T,
    values: readonly T[],
    onChange: (value: T) => void,
    labelOf: (value: T) => string = (v) => v || "—",
  ) => {
    const options: Option<T>[] = values.map((v) => ({ value: v, label: labelOf(v) }));
    return (
      <FormField label={label}>
        {({ labelId }) => (
          <Select<Option<T>, false>
            inputId={labelId}
            value={options.find((option) => option.value === value) ?? null}
            options={options}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            onChange={(selected) => selected && onChange(selected.value)}
          />
        )}
      </FormField>
    );
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        material ? `${i18n.translation.common.edit} ${material.name}` : `${i18n.translation.common.add} ${t.material}`
      }
      className={styles.modal}
      isSubmitting={isSubmitting}
      buttons={[
        <Button key="cancel" variant="secondary" onClick={onClose}>
          {i18n.translation.common.cancel}
        </Button>,
        <Button key="save" onClick={save}>
          {i18n.translation.common.save}
        </Button>,
      ]}
    >
      <div className={styles.grid}>
        <FormField label={i18n.translation.common.name}>
          {({ labelId }) => (
            <Input id={labelId} value={body.name} onChange={(event) => update({ name: event.target.value })} />
          )}
        </FormField>
        {TRANSLATIONS.map(([suffix, language]) =>
          text(`${i18n.translation.common.name} (${language})`, `name${suffix}`),
        )}
        <FormField label={m.manufacturer} isOptional>
          {({ labelId }) => (
            <Input
              id={labelId}
              value={body.manufacturer ?? ""}
              onChange={(event) => update({ manufacturer: event.target.value || null })}
            />
          )}
        </FormField>
        {select(
          i18n.translation.common.category,
          body.category,
          categories.map((c) => c.code),
          (category) =>
            // the subcategory belongs to its category: another category clears it
            update({ category, subcategory: category === body.category ? body.subcategory : null }),
          (code) => categories.find((c) => c.code === code)?.name ?? code,
        )}
        <FormField label={t.subcategory} isOptional>
          {({ labelId }) => {
            const options: Option<string>[] = [{ value: "", label: "—" }].concat(
              subcategories.filter((s) => s.category === body.category).map((s) => ({ value: s.code, label: s.name })),
            );
            return (
              <Select<Option<string>, false>
                inputId={labelId}
                value={options.find((option) => option.value === (body.subcategory ?? "")) ?? null}
                options={options}
                getOptionValue={(option) => option.value}
                getOptionLabel={(option) => option.label}
                onChange={(selected) => update({ subcategory: selected?.value || null })}
              />
            );
          }}
        </FormField>
        {number(`${m.density} [kg/m³]`, "density", 1)}
        {number(`${m.lambdaDry} [W/(m·K)]`, "lambdaDry", 4)}
        {number(`${m.cp} [J/(kg·K)]`, "cp", 0)}
        {number(m.mu, "mu", 2, true)}
        {number(`${m.wCap} [kg/m³]`, "wCap", 1, true)}
        {number(m.emissivity, "emissivity", 2)}
        {select(
          t.condCategory,
          body.condCategory,
          condCategories.map((c) => c.code),
          (condCategory) => update({ condCategory }),
          (code) => {
            const c = condCategories.find((other) => other.code === code);
            return c ? `${c.name} (${c.rule})` : code;
          },
        )}
        <FormField label={t.hatch} isOptional>
          {({ labelId }) => (
            <Input
              id={labelId}
              value={body.hatch ?? ""}
              onChange={(event) => update({ hatch: event.target.value || null })}
            />
          )}
        </FormField>
        {select<ThicknessMode>(t.thicknessMode, body.thicknessMode, ["variable", "fixed"], (thicknessMode) =>
          update({ thicknessMode }),
        )}
        {number(`${t.defaultThickness} [mm]`, "defaultThicknessMm", 2, true)}
        {body.thicknessMode === "fixed" ? (
          <FormField label={`${t.knownThicknesses} [mm]`}>
            {({ labelId }) => (
              <Input
                id={labelId}
                value={knownText}
                placeholder="12.5, 15, 18"
                onChange={(event) => setKnownText(event.target.value)}
              />
            )}
          </FormField>
        ) : (
          <>
            {number(`${t.minThickness} [mm]`, "minThicknessMm", 2, true)}
            {number(`${t.maxThickness} [mm]`, "maxThicknessMm", 2, true)}
          </>
        )}

        <h4 className={styles.wide}>{t.sectionMembrane}</h4>
        {number(t.sdVariableMin, "sdVariableMin", 3, true)}
        {number(t.sdVariableMax, "sdVariableMax", 3, true)}
        {number(t.dryCupPerm, "dryCupPerm", 3, true)}

        <h4 className={styles.wide}>{t.sectionTestDatabase}</h4>
        {number("θ_por [m³/m³]", "hygricThetaPor", 4, true)}
        {number("θ_eff [m³/m³]", "hygricThetaEff", 4, true)}
        {number("θ_cap [m³/m³]", "hygricThetaCap", 4, true)}
        {number("θ_80 [m³/m³]", "hygricTheta80", 4, true)}
        {number("A_w [kg/(m²·s^0.5)]", "hygricAw", 4, true)}

        <h4 className={styles.wide}>{t.sectionCurves}</h4>
        <p className={styles.wide}>{t.curvesHint}</p>
        {CURVES.map((c) => (
          <div key={c.field} className={styles.wide}>
            <FormField
              label={t[c.field]}
              isOptional
              helpLabel={c.field === "sdHumidityCurve" ? t.sdCurveHint : undefined}
            >
              {({ labelId }) => (
                <textarea
                  id={labelId}
                  className={styles.textarea}
                  value={curveTexts[c.field]}
                  placeholder={`[{"${c.x}": 0, "${c.y}": 0}, {"${c.x}": 1, "${c.y}": 1}]`}
                  onChange={(event) => setCurveTexts((current) => ({ ...current, [c.field]: event.target.value }))}
                />
              )}
            </FormField>
          </div>
        ))}

        <h4 className={styles.wide}>{t.sectionProvenance}</h4>
        {text(t.datasheetUrl, "datasheetUrl")}
        {text(t.productPageUrl, "productPageUrl")}
        <FormField label={t.datasheetChecked} isOptional>
          {({ labelId }) => (
            <Input
              id={labelId}
              type="date"
              value={body.datasheetChecked ?? ""}
              onChange={(event) => update({ datasheetChecked: event.target.value || null })}
            />
          )}
        </FormField>
        {text(t.formerlyNamed, "formerlyNamed")}
        {text(t.sourceDatabase, "sourceDatabase")}
        {text(t.sourceLaboratory, "sourceLaboratory")}
        {text(t.sourceDate, "sourceDate")}
        {text(t.sourceFile, "sourceFile")}
        {fieldChecks(t.assumedFields, "assumedFields")}
        {fieldChecks(t.derivedFields, "derivedFields")}
        {select(t.dataNote, body.dataNote ?? "", DATA_NOTES, (dataNote) =>
          update({
            dataNote: dataNote || null,
            dataNoteLambda: dataNote === "lambdaBelowAir" ? body.dataNoteLambda : null,
          }),
        )}
        {body.dataNote === "lambdaBelowAir" ? number(t.dataNoteLambda, "dataNoteLambda", 4) : null}
      </div>
    </ModalDialog>
  );
};

export default MaterialFormDialog;
